import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Save } from "lucide-react";
import { api, errMsg } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Card } from "../components/ui/card";
import { toast } from "sonner";
import { Breadcrumb } from "../components/Breadcrumb";

const EMPTY = { nama_keluarga: "", nomor_kk: "", alamat: "", rt: "", rw: "", kelurahan: "", kecamatan: "", kota: "", kode_pos: "", keterangan: "" };

export default function KeluargaFormPage() {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = Boolean(id);
  const [form, setForm] = useState(EMPTY);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (isEdit) {
      api.get(`/keluarga/${id}`).then((r) => {
        const d = r.data;
        const next = { ...EMPTY };
        Object.keys(EMPTY).forEach((k) => { next[k] = d[k] ?? ""; });
        setForm(next);
      }).catch((e) => toast.error(errMsg(e)));
    }
  }, [id, isEdit]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    if (!form.nama_keluarga.trim()) { setError("Nama keluarga wajib diisi"); return; }
    setSaving(true);
    try {
      const payload = {};
      Object.entries(form).forEach(([k, v]) => { payload[k] = v === "" ? null : v; });
      payload.nama_keluarga = form.nama_keluarga.trim();
      let savedId = id;
      if (isEdit) await api.put(`/keluarga/${id}`, payload);
      else { const r = await api.post("/keluarga", payload); savedId = r.data.id; }
      toast.success(isEdit ? "Keluarga diperbarui" : "Keluarga baru tersimpan");
      navigate(`/keluarga/${savedId}`);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto space-y-5">
      <Breadcrumb items={[{ label: "Data Keluarga" }, { label: "Kartu Keluarga", to: "/keluarga" }, { label: isEdit ? "Edit" : "Tambah" }]} />
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="icon" className="rounded-xl" onClick={() => navigate(-1)} data-testid="back-btn"><ArrowLeft className="h-5 w-5" /></Button>
        <h1 className="text-2xl font-bold tracking-tight text-slate-900">{isEdit ? "Edit Kartu Keluarga" : "Tambah Kartu Keluarga"}</h1>
      </div>

      <Card className="p-5 rounded-2xl space-y-4">
        <Field label="Nama Keluarga" required error={error}>
          <Input data-testid="input-nama-keluarga" value={form.nama_keluarga} onChange={(e) => { set("nama_keluarga", e.target.value); setError(""); }} placeholder="Contoh: Keluarga Bpk. Yohanes" className="min-h-[44px] rounded-xl" />
        </Field>
        <Field label="Nomor KK (opsional)">
          <Input data-testid="input-kel-nomor-kk" value={form.nomor_kk} onChange={(e) => set("nomor_kk", e.target.value)} className="min-h-[44px] rounded-xl font-mono" />
        </Field>
        <Field label="Alamat"><Textarea data-testid="input-kel-alamat" value={form.alamat} onChange={(e) => set("alamat", e.target.value)} className="rounded-xl" rows={2} /></Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="RT"><Input data-testid="input-kel-rt" value={form.rt} onChange={(e) => set("rt", e.target.value)} className="min-h-[44px] rounded-xl" /></Field>
          <Field label="RW"><Input data-testid="input-kel-rw" value={form.rw} onChange={(e) => set("rw", e.target.value)} className="min-h-[44px] rounded-xl" /></Field>
        </div>
        <Field label="Kelurahan"><Input data-testid="input-kel-kelurahan" value={form.kelurahan} onChange={(e) => set("kelurahan", e.target.value)} className="min-h-[44px] rounded-xl" /></Field>
        <Field label="Kecamatan"><Input data-testid="input-kel-kecamatan" value={form.kecamatan} onChange={(e) => set("kecamatan", e.target.value)} className="min-h-[44px] rounded-xl" /></Field>
        <Field label="Kota/Kabupaten"><Input data-testid="input-kel-kota" value={form.kota} onChange={(e) => set("kota", e.target.value)} className="min-h-[44px] rounded-xl" /></Field>
        <Field label="Kode Pos"><Input data-testid="input-kel-kode-pos" value={form.kode_pos} onChange={(e) => set("kode_pos", e.target.value)} className="min-h-[44px] rounded-xl font-mono" /></Field>
        <Field label="Keterangan"><Textarea data-testid="input-kel-keterangan" value={form.keterangan} onChange={(e) => set("keterangan", e.target.value)} className="rounded-xl" rows={2} /></Field>
      </Card>

      <div className="flex gap-3">
        <Button variant="outline" className="flex-1 rounded-xl min-h-[48px]" onClick={() => navigate(-1)} data-testid="cancel-btn">Batal</Button>
        <Button className="flex-1 rounded-xl min-h-[48px] bg-[hsl(224,64%,33%)] hover:bg-[hsl(224,64%,28%)]" onClick={submit} disabled={saving} data-testid="save-keluarga-btn">
          <Save className="h-4 w-4 mr-2" /> {saving ? "Menyimpan..." : "Simpan"}
        </Button>
      </div>
    </div>
  );
}

const Field = ({ label, required, error, children }) => (
  <div>
    <Label className="text-sm font-medium text-slate-700 mb-1.5 block">{label} {required && <span className="text-red-500">*</span>}</Label>
    {children}
    {error && <p className="text-xs text-red-500 mt-1" data-testid="field-error">{error}</p>}
  </div>
);
